import { canvasManager } from "appworks/graphics/canvas/canvas-manager";
import { gameState } from "appworks/model/game-state";
import { Gameplay } from "appworks/model/gameplay/gameplay";
import { Record } from "appworks/model/gameplay/records/record";
import { RecordBuilder } from "appworks/server/builders/record-builder";
import { Services } from "appworks/services/services";
import { PartnerConnect, PartnerConnectManager } from "partner-connect/partner-connect-manager";
import { slotModel } from "slotworks/model/slot-model";

export class PartnerConnectConfigRecordBuilder implements RecordBuilder {

    public id: string = "PartnerConnectConfigRecordBuilder";

    public filter(request: any, config: PartnerConnect.SlotGameConfig): boolean {
        if (config?.mode && config?.settings && config?.wagers) {
            return true;
        }
        return false;
    }

    public build(gameplay: Gameplay, request: any, config: PartnerConnect.SlotGameConfig): Record[] {
        this.setCurrency(config);
        this.setLanguage(config);
        this.setJurisdiction(config);
        this.setBets(config);
        this.setAutoplayLimits(config);
        this.setHistoryData(config);
        return [];
    }

    protected setCurrency(config: PartnerConnect.SlotGameConfig): void {
        Services.currency.configure(config.settings.language, config.settings.currency, "symbol");
    }

    protected setLanguage(config: PartnerConnect.SlotGameConfig): void {
        Services.translations.setLanguage(config.settings.language);
    }

    protected setJurisdiction(config: PartnerConnect.SlotGameConfig): void {
        Services.jurisdiction.setMinimumSpinTimes([{
            minBet: 0,
            delayMs: config.settings.minimalSpinningTime * 1000
        }]);

        if (PartnerConnectManager.getConfig().customParameters.disableSkip === "yes" || config.settings.minimalSpinningTime > 0) {
            Services.jurisdiction.setSpinSkippingEnabled(false);
        }
    }

    protected setBets(config: PartnerConnect.SlotGameConfig): void {
        const model = slotModel.read();

        const validBets: number[] =
            config.settings.organizationConfig.Denoms.split(":")[1]
                .split(",").map((x) => Math.round(Number(x) * 100 * config.settings.slotConfig.CoinsPerBet[0]));
        const startingBet: number = (config.wagers.data.length > 0 && config.wagers.data[0].bets.length > 0) ?
            config.wagers.data[0].bets[0].betdata.cashBet :
            config.settings.defaultBetValue * config.settings.slotConfig.CoinsPerBet[0];
        const defaultBet: number = Math.round(startingBet * 100);

        model.bet.creditSizes.values = validBets;
        model.bet.creditSizes.defaultValue = defaultBet;
        model.bet.creditSizes.currentValue = defaultBet;
        model.bet.creditsPerLine.values = [1];
        model.bet.creditsPerLine.defaultValue = 1;
        model.bet.creditsPerLine.currentValue = 1;
        model.bet.selectedLines = [1];
        model.bet.fixedBetCost = 1;

        canvasManager.fullScreenEnabled = true;
        if (config.customParameters.fullscreen === "no") {
            canvasManager.fullScreenEnabled = false;
        }

        slotModel.write(model);
    }

    protected setAutoplayLimits(config: PartnerConnect.SlotGameConfig): void {
        const model = slotModel.read();

        const params = new URLSearchParams(window.location.search);
        const urlParamAutoplayLimits = params.get("autoplayLevels");

        if (urlParamAutoplayLimits || PartnerConnectManager.OVERRIDE_AUTOPLAY_COUNTS === undefined) {
            const autoplayLevels = config.settings.autoplayLevels.map((x) => (x === -1) ? Infinity : x);
            model.autoplay.autoplays.defaultValue = autoplayLevels[2];
            model.autoplay.autoplays.values = autoplayLevels;
        } else {
            let counts: number[];
            if (config.settings.hasLossLimit) {
                counts = PartnerConnectManager.OVERRIDE_AUTOPLAY_COUNTS.lossLimitEnabled;
            } else {
                counts = PartnerConnectManager.OVERRIDE_AUTOPLAY_COUNTS.normal;
            }
            model.autoplay.autoplays.defaultValue = counts[PartnerConnectManager.OVERRIDE_AUTOPLAY_COUNTS.defaultIndex];
            model.autoplay.autoplays.values = counts;
        }

        slotModel.write(model);

        let currentBet: number = 0;

        const updateAutoplayLimits = () => {
            const limitsModel = slotModel.read();
            const bet = limitsModel.bet.creditSizes.currentValue;
            if (currentBet !== bet) {
                currentBet = bet;

                if (PartnerConnectManager.OVERRIDE_AUTOPLAY_LOSS_LIMITS === undefined) {
                    const max = config.settings.hasLossLimit ? bet * 100 : Number.MAX_VALUE;
                    limitsModel.autoplay.lossLimit.values = [bet * 5, bet * 10, bet * 20, bet * 50, max];
                    limitsModel.autoplay.lossLimit.defaultValue = max;
                    limitsModel.autoplay.singleWinLimit.values = [bet * 10, bet * 20, bet * 100, bet * 200, Number.MAX_VALUE];
                    limitsModel.autoplay.singleWinLimit.defaultValue = Number.MAX_VALUE;
                } else {
                    let lossLimits: number[];
                    let singleWinLimits: number[];
                    if (config.settings.hasLossLimit) {
                        lossLimits = PartnerConnectManager.OVERRIDE_AUTOPLAY_LOSS_LIMITS.lossLimitEnabled;
                        singleWinLimits = PartnerConnectManager.OVERRIDE_AUTOPLAY_SINGLE_WIN_LIMITS.lossLimitEnabled;
                    } else {
                        lossLimits = PartnerConnectManager.OVERRIDE_AUTOPLAY_LOSS_LIMITS.normal;
                        singleWinLimits = PartnerConnectManager.OVERRIDE_AUTOPLAY_SINGLE_WIN_LIMITS.normal;
                    }
                    limitsModel.autoplay.lossLimit.values = lossLimits.map((el) => (el === Number.MAX_VALUE) ? el : el * (bet / 100));
                    limitsModel.autoplay.lossLimit.defaultValue = limitsModel.autoplay.lossLimit.values[
                        PartnerConnectManager.OVERRIDE_AUTOPLAY_LOSS_LIMITS.defaultIndex
                    ];
                    limitsModel.autoplay.singleWinLimit.values = singleWinLimits.map((el) => (el === Number.MAX_VALUE) ? el : el * (bet / 100));
                    limitsModel.autoplay.singleWinLimit.defaultValue = limitsModel.autoplay.lossLimit.values[
                        PartnerConnectManager.OVERRIDE_AUTOPLAY_SINGLE_WIN_LIMITS.defaultIndex
                    ];
                }

                slotModel.write(limitsModel);
            }
        };

        updateAutoplayLimits();

        slotModel.onUpdate.add(updateAutoplayLimits);
    }

    protected setHistoryData(config: PartnerConnect.SlotGameConfig) {
        if (config.wagers?.data?.length > 0) {
            const wager = config.wagers.data[0];
            PartnerConnectManager.historyData = {};
            PartnerConnectManager.historyData.wagerid = wager.wagerid;
            PartnerConnectManager.historyData.bet = wager.bets[0].betdata.cashBet * 100;
            PartnerConnectManager.historyData.startBalance = wager.bets[0].betdata.initialBalance * 100;
            PartnerConnectManager.historyData.win = parseFloat(wager.bets[wager.bets.length - 1].wonamount) * 100;
            PartnerConnectManager.historyData.endBalance =
                PartnerConnectManager.historyData.startBalance - PartnerConnectManager.historyData.bet + PartnerConnectManager.historyData.win;
        }
    }
}

export const partnerConnectConfigRecordBuilder = new PartnerConnectConfigRecordBuilder();
